'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { signIn } from 'next-auth/react'
import { Eye, EyeOff, Mail, Lock, User, Search, Zap, ShoppingCart, BarChart2, Package, Phone, IdCard } from 'lucide-react'
import { PrimodropLogo } from '@/components/primodrop-logo'
import { toast } from 'sonner'
import { formatCpfCnpjInput, formatPhoneInput } from '@/lib/br-validation'

const features = [
  { Icon: Search, title: 'Produtos vencedores', desc: 'Encontre os melhores produtos com alta margem de lucro.' },
  { Icon: Zap, title: 'Publicação automática', desc: 'Publique anúncios no Mercado Livre em segundos.' },
  { Icon: ShoppingCart, title: 'Gestão de pedidos', desc: 'Importe pedidos e envie para os fornecedores com um clique.' },
  { Icon: BarChart2, title: 'Relatórios inteligentes', desc: 'Acompanhe vendas, lucros e métricas em tempo real.' },
]

export default function RegisterPage() {
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    cpfCnpj: '',
    password: '',
    confirmPassword: '',
  })
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [checkingPhone, setCheckingPhone] = useState(false)
  const [googleAvailable, setGoogleAvailable] = useState(false)
  const [checkingGoogle, setCheckingGoogle] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/auth/google/status')
        const body = await res.json()
        if (!cancelled) setGoogleAvailable(!!body.available)
      } catch {
        if (!cancelled) setGoogleAvailable(false)
      } finally {
        if (!cancelled) setCheckingGoogle(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  async function handleGoogleSignUp() {
    if (!googleAvailable) {
      toast.error('Cadastro com Google indisponível. Peça ao administrador para configurar.')
      return
    }
    await signIn('google', { callbackUrl: '/dashboard/configuracoes?completar=1' })
  }

  function set(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }))
    if (field === 'phone') setPhoneError(null)
  }

  async function validateWhatsAppPhone(value?: string): Promise<{ ok: boolean; message?: string }> {
    const phone = (value ?? form.phone).trim()
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      const message = 'Informe um WhatsApp com DDD.'
      setPhoneError(message)
      return { ok: false, message }
    }
    setCheckingPhone(true)
    try {
      const res = await fetch('/api/auth/verify-whatsapp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      })
      const data = await res.json().catch(() => ({}))
      // Offline: permite cadastro (só envios ficam bloqueados depois)
      if (res.ok && data.unavailable) {
        setPhoneError(null)
        return { ok: true }
      }
      if (!res.ok) {
        const message = data.message || 'WhatsApp inválido'
        setPhoneError(message)
        return { ok: false, message }
      }
      setPhoneError(null)
      return { ok: true }
    } catch {
      // Falha de rede na validação: não bloqueia cadastro
      setPhoneError(null)
      return { ok: true }
    } finally {
      setCheckingPhone(false)
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (form.password !== form.confirmPassword) {
      toast.error('As senhas não coincidem')
      return
    }

    const wa = await validateWhatsAppPhone()
    if (!wa.ok) {
      toast.error(wa.message || 'Informe um número de WhatsApp válido')
      return
    }

    setIsLoading(true)
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          cpfCnpj: form.cpfCnpj,
          password: form.password,
        }),
      })

      const data = await res.json()

      if (res.ok) {
        const loginResult = await signIn('credentials', {
          email: form.email,
          password: form.password,
          redirect: false,
        })

        if (loginResult?.error) {
          toast.success('Cadastro realizado! Faça login para continuar.')
          router.push('/login')
          return
        }

        toast.success('Cadastro realizado! Bem-vindo ao Primodrop.')
        router.push('/dashboard')
        router.refresh()
      } else {
        const firstError = Array.isArray(data.errors)
          ? data.errors[0]?.message
          : null
        toast.error(firstError || data.message || 'Erro ao realizar cadastro')
      }
    } catch {
      toast.error('Erro de conexão. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      {/* ───────────────── Left dark panel (coded, crisp) ───────────────── */}
      <div className="hidden lg:flex lg:w-1/2 bg-black flex-col justify-center px-16 py-14 relative overflow-hidden">
        {/* Wireframe open-box, top-right (partially off screen) */}
        <svg
          className="absolute -top-10 right-0 w-[340px] h-[340px] pointer-events-none select-none"
          viewBox="0 0 200 200"
          fill="none"
          aria-hidden
        >
          <g stroke="#ffffff" strokeWidth="1.4" opacity="0.14" strokeLinejoin="round">
            <path d="M100 55 L160 85 L160 150 L100 180 L40 150 L40 85 Z" />
            <path d="M100 55 L100 120 M100 120 L160 150 M100 120 L40 150" />
            <path d="M100 55 L60 35 L120 5 L160 85" />
            <path d="M100 55 L140 35 L80 5 L40 85" />
          </g>
        </svg>

        {/* 3D matte black box with PRIMODROP printed on its side */}
        <div
          className="absolute pointer-events-none select-none"
          style={{ right: '-190px', bottom: '-170px' }}
          aria-hidden
        >
          <div
            className="relative"
            style={{
              transform: 'perspective(1400px) rotateX(14deg) rotateY(-40deg) rotateZ(3deg)',
              transformStyle: 'preserve-3d',
            }}
          >
            <div className="w-[34rem] h-[34rem] bg-gradient-to-br from-[#151515] via-[#0d0d0d] to-[#040404] border border-white/[0.06] relative shadow-[0_60px_120px_rgba(0,0,0,0.85)]">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex items-center text-white/25 scale-[3.2]">
                  <span className="text-2xl font-black tracking-tight">PRIMODR</span>
                  <Package className="w-5 h-5 mx-0.5 stroke-[2.5]" />
                  <span className="text-2xl font-black tracking-tight">P</span>
                </div>
              </div>
              <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-white/[0.04]" />
            </div>
            <div
              className="absolute top-0 left-0 w-[34rem] h-[34rem] bg-[#1d1d1d] border border-white/[0.06]"
              style={{ transform: 'translate(-46px, -46px)' }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-lg">
          {/* Logo */}
          <PrimodropLogo variant="white" height={52} href={null} />

          {/* Title */}
          <h2 className="mt-10 text-[2.5rem] font-bold text-white leading-[1.12] tracking-tight">
            A plataforma completa para voce vender sem estoque no Mercado Livre.
          </h2>

          {/* Description */}
          <p className="mt-6 text-gray-400 text-[15px] leading-relaxed max-w-md">
            Encontre produtos vencedores, publique anúncios em segundos e gerencie seus pedidos com automação e inteligência.
          </p>

          {/* Features */}
          <div className="mt-12 space-y-6">
            {features.map(({ Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/[0.03] border border-white/10 rounded-xl flex items-center justify-center shrink-0">
                  <Icon className="w-[18px] h-[18px] text-white" strokeWidth={1.6} />
                </div>
                <div className="pt-1">
                  <p className="text-white font-semibold text-[15px]">{title}</p>
                  <p className="text-gray-400 text-[13px] mt-1 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ───────────────── Right light panel ───────────────── */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-between bg-[#F9F9F9] py-12 px-6 sm:px-8">
        {/* Mobile logo */}
        <div className="lg:hidden w-full max-w-md flex justify-center">
          <PrimodropLogo variant="blackBg" height={40} href={null} />
        </div>

        <div className="w-full max-w-md flex-1 flex flex-col justify-center py-12">
          {/* Register card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-[0_10px_40px_rgba(0,0,0,0.06)] p-8 sm:p-10 space-y-6">
            <div className="text-center space-y-2">
              <h1 className="text-[1.75rem] font-bold text-gray-900 leading-tight">Criar conta</h1>
              <p className="text-sm text-gray-500">Preencha os dados abaixo para começar.</p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              {/* Nome */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600">Nome</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" strokeWidth={1.5} />
                  <input
                    type="text"
                    placeholder="Seu nome completo"
                    value={form.name}
                    onChange={e => set('name', e.target.value)}
                    required
                    minLength={3}
                    className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 placeholder-gray-300 transition"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600">E-mail</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" strokeWidth={1.5} />
                  <input
                    type="email"
                    placeholder="seu@email.com"
                    value={form.email}
                    onChange={e => set('email', e.target.value)}
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 placeholder-gray-300 transition"
                  />
                </div>
              </div>

              {/* WhatsApp */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600">WhatsApp</label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" strokeWidth={1.5} />
                  <input
                    type="tel"
                    inputMode="numeric"
                    placeholder="(11) 99999-9999"
                    value={form.phone}
                    onChange={e => set('phone', formatPhoneInput(e.target.value))}
                    onBlur={() => { void validateWhatsAppPhone() }}
                    required
                    aria-invalid={!!phoneError}
                    className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 placeholder-gray-300 transition"
                  />
                </div>
                {checkingPhone ? (
                  <p className="text-[11px] text-gray-400">Validando WhatsApp…</p>
                ) : phoneError ? (
                  <p className="text-[11px] text-red-600">{phoneError}</p>
                ) : (
                  <p className="text-[11px] text-gray-400">Usaremos este número para avisos e suporte.</p>
                )}
              </div>

              {/* CPF / CNPJ */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600">CPF ou CNPJ</label>
                <div className="relative">
                  <IdCard className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" strokeWidth={1.5} />
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                    value={form.cpfCnpj}
                    onChange={e => set('cpfCnpj', formatCpfCnpjInput(e.target.value))}
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 placeholder-gray-300 transition"
                  />
                </div>
              </div>

              {/* Senha */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600">Senha</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" strokeWidth={1.5} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mínimo 6 caracteres"
                    value={form.password}
                    onChange={e => set('password', e.target.value)}
                    required
                    minLength={6}
                    className="w-full pl-11 pr-11 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 placeholder-gray-300 transition"
                  />
                  <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="w-[18px] h-[18px]" strokeWidth={1.5} /> : <Eye className="w-[18px] h-[18px]" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              {/* Confirmar Senha */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600">Confirmar senha</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" strokeWidth={1.5} />
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Repita a senha"
                    value={form.confirmPassword}
                    onChange={e => set('confirmPassword', e.target.value)}
                    required
                    className="w-full pl-11 pr-11 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 placeholder-gray-300 transition"
                  />
                  <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showConfirm ? <EyeOff className="w-[18px] h-[18px]" strokeWidth={1.5} /> : <Eye className="w-[18px] h-[18px]" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-black text-white font-semibold rounded-lg text-sm hover:bg-gray-900 transition-colors disabled:opacity-60"
              >
                {isLoading ? 'Cadastrando...' : 'Criar conta'}
              </button>
            </form>

            {googleAvailable && (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400">ou continue com</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                <button
                  type="button"
                  onClick={handleGoogleSignUp}
                  disabled={checkingGoogle}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
                >
                  <GoogleIcon />
                  Cadastrar com Google
                </button>
              </>
            )}

            <p className="text-center text-sm text-gray-500 pt-1">
              Já tem uma conta?{' '}
              <a href="/login" className="font-bold text-gray-900 hover:underline">
                Entrar
              </a>
            </p>
          </div>
        </div>

        <p className="text-xs text-gray-400 text-center">
          © {new Date().getFullYear()} Primodrop. Todos os direitos reservados.
        </p>
      </div>
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  )
}
